import { LitElement, html, css } from 'lit';

// Helper to escape regex special chars
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export class UITagInput extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true }, // Comma-separated list of tags
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    options: { type: Array }, // Autocomplete recommendations
    separator: { type: String }, // Default ','
    maxTags: { type: Number },
    label: { type: String, reflect: true },
    
    // Internal States
    tags: { type: Array, state: true },
    inputValue: { type: String, state: true },
    isOpen: { type: Boolean, state: true },
    highlightedIndex: { type: Number, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-weight: 600;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .taginput-container {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      padding: 6px var(--ui-padding-x);
      min-height: var(--ui-input-height);
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      box-sizing: border-box;
      cursor: text;
    }

    .taginput-container:hover:not(.disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .taginput-container.focused:not(.disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .taginput-container.disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    /* Tag Chip styling */
    .tag-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px;
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      border-radius: var(--ui-radius-sm);
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      animation: zoomIn 150ms cubic-bezier(0.16, 1, 0.3, 1);
      user-select: none;
    }

    @keyframes zoomIn {
      from { transform: scale(0.85); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }

    .tag-chip-remove {
      border: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.8;
      transition: var(--ui-transition-fast);
      border-radius: var(--ui-radius-full);
      width: 14px;
      height: 14px;
    }

    .tag-chip-remove:hover {
      opacity: 1;
      background-color: rgba(255, 255, 255, 0.2);
    }

    /* Input inside container */
    .taginput-input {
      flex: 1;
      min-width: 80px;
      border: none;
      background: transparent;
      outline: none;
      color: inherit;
      font-family: inherit;
      font-size: inherit;
      padding: 0;
      height: 28px;
      box-sizing: border-box;
    }

    .taginput-input:disabled {
      cursor: not-allowed;
    }

    /* Popover suggestions panel */
    .suggestions-panel {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 100%;
      max-height: 200px;
      overflow-y: auto;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-popover);
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
      box-sizing: border-box;
      padding: 6px;
    }

    .suggestions-panel.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    .suggestion-option {
      padding: 8px 12px;
      border-radius: var(--ui-radius-sm);
      cursor: pointer;
      font-size: var(--ui-font-size-base);
      transition: var(--ui-transition-fast);
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: var(--ui-text);
    }

    .suggestion-option:hover {
      background-color: var(--ui-surface-hover);
    }

    .suggestion-option.highlighted {
      background-color: var(--ui-surface-active);
      color: var(--ui-primary);
    }

    .highlight {
      font-weight: 700;
      color: var(--ui-primary);
      text-decoration: underline;
    }

    .empty-state {
      padding: 10px;
      text-align: center;
      color: var(--ui-text-muted);
      font-size: var(--ui-font-size-sm);
    }

    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: calc(var(--ui-z-popover) - 1);
    }

    .taginput-container.open-panel .backdrop {
      display: block;
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = 'Ajouter des tags...';
    this.disabled = false;
    this.required = false;
    this.options = [];
    this.separator = ',';
    this.maxTags = Infinity;
    this.label = '';

    this.tags = [];
    this.inputValue = '';
    this.isOpen = false;
    this.highlightedIndex = -1;
    this.isFocused = false;

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  willUpdate(changedProperties) {
    if (typeof this.options === 'string') {
      try {
        this.options = JSON.parse(this.options);
      } catch (e) {
        this.options = [];
      }
    }

    if (changedProperties.has('value')) {
      this._syncTagsFromValue();
    }
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasTags = this.tags && this.tags.length > 0;
    if (this.required && !hasTags) {
      this.internals.setValidity(
        { valueMissing: true },
        'Veuillez ajouter au moins un élément.',
        this.shadowRoot.querySelector('.taginput-input')
      );
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  _syncTagsFromValue() {
    if (this.value) {
      this.tags = this.value.split(this.separator).map(t => t.trim()).filter(t => t !== '');
    } else {
      this.tags = [];
    }
  }

  _syncValueFromTags() {
    const newVal = this.tags.join(this.separator);
    if (newVal !== this.value) {
      this.value = newVal;
      this._dispatchChangeEvent(newVal);
    }
  }

  _dispatchChangeEvent(val) {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: val },
      bubbles: true,
      composed: true
    }));
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closePanel();
    }
  }

  _focusInput() {
    if (this.disabled) return;
    this.shadowRoot.querySelector('.taginput-input')?.focus();
  }

  _handleFocus() {
    this.isFocused = true;
    this.requestUpdate();
    if (Array.isArray(this.options) && this.options.length > 0) {
      this.isOpen = true;
    }
  }

  _handleBlur() {
    // Small delay to allow clicking suggestions
    setTimeout(() => {
      this.isFocused = false;
      this.requestUpdate();
    }, 150);
  }

  _closePanel() {
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  _handleInput(e) {
    this.inputValue = e.target.value;
    if (Array.isArray(this.options) && this.options.length > 0) {
      this.isOpen = true;
    }
    this.highlightedIndex = -1;
  }

  _addTag(tagText) {
    const text = tagText.trim();
    if (!text) return;
    if (this.tags.includes(text)) {
      this.inputValue = '';
      return;
    }
    if (this.tags.length >= this.maxTags) {
      this.inputValue = '';
      return;
    }

    this.tags = [...this.tags, text];
    this.inputValue = '';
    this._syncValueFromTags();
    this._closePanel();
  }

  _removeTag(index) {
    if (this.disabled) return;
    this.tags = this.tags.filter((_, i) => i !== index);
    this._syncValueFromTags();
  }

  _handleKeyDown(e) {
    if (this.disabled) return;
    const filtered = this._getFilteredOptions();

    if (e.key === 'Enter') {
      e.preventDefault();
      if (this.isOpen && this.highlightedIndex >= 0 && this.highlightedIndex < filtered.length) {
        const selectedOpt = filtered[this.highlightedIndex];
        const val = typeof selectedOpt === 'object' && selectedOpt !== null ? selectedOpt.value : selectedOpt;
        this._addTag(String(val));
      } else if (this.inputValue.trim()) {
        this._addTag(this.inputValue);
      }
    } else if (e.key === 'Backspace') {
      if (!this.inputValue && this.tags.length > 0) {
        this._removeTag(this.tags.length - 1);
      }
    } else if (e.key === 'ArrowDown') {
      if (this.isOpen && filtered.length > 0) {
        e.preventDefault();
        this.highlightedIndex = (this.highlightedIndex + 1) % filtered.length;
      } else if (!this.isOpen && Array.isArray(this.options) && this.options.length > 0) {
        this.isOpen = true;
      }
    } else if (e.key === 'ArrowUp') {
      if (this.isOpen && filtered.length > 0) {
        e.preventDefault();
        this.highlightedIndex = (this.highlightedIndex - 1 + filtered.length) % filtered.length;
      }
    } else if (e.key === 'Escape') {
      if (this.isOpen) {
        e.preventDefault();
        this._closePanel();
      }
    }
  }

  _getOptionLabel(opt) {
    if (typeof opt === 'object' && opt !== null) {
      return opt.label || String(opt.value);
    }
    return String(opt);
  }

  _getOptionValue(opt) {
    if (typeof opt === 'object' && opt !== null) {
      return opt.value;
    }
    return String(opt);
  }

  _getFilteredOptions() {
    if (!Array.isArray(this.options)) return [];
    
    // Filter out options that are already selected in tags
    const available = this.options.filter(opt => {
      const val = String(this._getOptionValue(opt));
      return !this.tags.includes(val);
    });

    if (!this.inputValue) return available;
    const query = this.inputValue.toLowerCase();
    
    return available.filter(opt => {
      const label = this._getOptionLabel(opt).toLowerCase();
      return label.includes(query);
    });
  }

  _renderOptionLabelWithHighlight(opt) {
    const label = this._getOptionLabel(opt);
    if (!this.inputValue) return html`${label}`;
    
    const query = this.inputValue.trim();
    if (!query) return html`${label}`;

    try {
      const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
      const parts = label.split(regex);
      return html`
        ${parts.map(part => 
          regex.test(part) ? html`<span class="highlight">${part}</span>` : html`${part}`
        )}
      `;
    } catch (e) {
      return html`${label}`;
    }
  }

  render() {
    const filtered = this._getFilteredOptions();
    const showPlaceholder = this.tags.length === 0 && !this.inputValue;

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div 
        part="input" class="taginput-container ${this.isFocused ? 'focused' : ''} ${this.disabled ? 'disabled' : ''} ${this.isOpen ? 'open-panel' : ''}"
        @click="${this._focusInput}"
      >
        ${this.tags.map((tag, idx) => html`
          <div class="tag-chip">
            <span>${tag}</span>
            <button 
              type="button" 
              class="tag-chip-remove" 
              ?disabled="${this.disabled}"
              @click="${(e) => { e.stopPropagation(); this._removeTag(idx); }}"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        `)}

        <input
          type="text"
          class="taginput-input"
          .value="${this.inputValue}"
          placeholder="${showPlaceholder ? this.placeholder : ''}"
          ?disabled="${this.disabled}"
          @input="${this._handleInput}"
          @focus="${this._handleFocus}"
          @blur="${this._handleBlur}"
          @keydown="${this._handleKeyDown}"
        />

        <div class="backdrop" @click="${this._closePanel}"></div>

        ${Array.isArray(this.options) && this.options.length > 0 ? html`
          <div class="suggestions-panel ${this.isOpen && filtered.length > 0 ? 'open' : ''}">
            ${filtered.map((opt, idx) => {
              const isHighlighted = idx === this.highlightedIndex;
              return html`
                <div 
                  class="suggestion-option ${isHighlighted ? 'highlighted' : ''}"
                  @click="${(e) => { e.stopPropagation(); this._addTag(String(this._getOptionValue(opt))); }}"
                >
                  <span>${this._renderOptionLabelWithHighlight(opt)}</span>
                </div>
              `;
            })}
          </div>
        ` : ''}
      </div>
    `;
  }
}

if (!customElements.get('ui-taginput')) {
  customElements.define('ui-taginput', UITagInput);
}
